import type { ReactNode } from 'react'
import type z from 'zod/v4'
import { useTranslations } from '@aikoa/i18n/react'
import { Link } from 'expo-router'
import { createContext, useContext } from 'react'
import { View } from 'react-native'
import { Label } from '~/components/typography/Content'
import { Checkbox } from '~/components/ui/checkbox'
import { Input, type InputVariant } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import { type invoiceSchema } from '~/data/invoices/types'
import { cn } from '~/lib/utils'

interface InvoiceFormProps {
  showAgreement?: boolean
  isPending: boolean
  className?: string
  inputVariant?: InputVariant
}

export type FormType = z.infer<typeof invoiceSchema>

type TextFieldName = Exclude<keyof FormType, 'terms_agreement'>
type CheckboxFieldName = Extract<keyof FormType, 'terms_agreement'>

export const initialValues: FormType = {
  organisation_name: '',
  organisation_identifier: '',
  e_invoice_address: '',
  e_invoice_operator: '',
  reference: '',
  terms_agreement: false,
}

export const InvoiceFormContext = createContext<{
  values: FormType
  errors: Partial<Record<keyof FormType | 'form', string[]>>
  setValue: <Key extends keyof FormType>(key: Key, value: FormType[Key]) => void
  setError: (key: keyof FormType, errorMessage?: string) => void
  setErrors: (errors: Partial<Record<keyof FormType | 'form', string[]>>) => void
}>({
  values: initialValues,
  errors: {},
  setValue: () => {},
  setError: () => {},
  setErrors: () => {},
})

function TextField({
  label,
  description,
  multiline = false,
  name,
  inputVariant = 'line',
}: {
  label: string
  name: TextFieldName
  multiline?: boolean
  description?: string
  inputVariant?: InputVariant
}) {
  const { values, setValue, errors } = useContext(InvoiceFormContext)
  const error = errors?.[name]?.[0]
  const fieldValue = values[name]
  const fieldValueString = typeof fieldValue === 'string' ? fieldValue : ''

  return (
    <View className="w-full gap-2">
      <Label>{label}</Label>
      <Input
        value={fieldValueString}
        onChangeText={(value) => setValue(name, value)}
        multiline={multiline}
        placeholder={label}
        variant={inputVariant}
      />
      {description && <Text className="text-xs text-secondary">{description}</Text>}
      {error && <Text className="text-xs text-destructive">{error}</Text>}
    </View>
  )
}

function CheckboxField({
  label,
  description,
  className,
  name,
  inputVariant = 'line',
}: {
  name: CheckboxFieldName
  label: ReactNode
  description?: ReactNode
  className?: string
  inputVariant?: InputVariant
}) {
  const { values, setValue, errors } = useContext(InvoiceFormContext)
  const error = errors?.[name]?.[0]
  const isChecked = Boolean(values[name])

  return (
    <View className={cn('w-full gap-2', className)}>
      <View className="flex-row items-center gap-3">
        <Checkbox
          checked={isChecked}
          onCheckedChange={(checked) => {
            setValue(name, checked ?? false)
          }}
          variant={
            inputVariant === 'box'
              ? 'background'
              : inputVariant === 'line-dark'
                ? 'outline-dark'
                : 'outline'
          }
        />
        <Text className="flex-1 text-sm text-foreground">{label}</Text>
      </View>
      {description && <Text className="text-xs text-secondary">{description}</Text>}
      {error && <Text className="text-xs text-destructive">{error}</Text>}
    </View>
  )
}

export default function InvoiceForm({
  isPending,
  className,
  inputVariant = 'line',
  showAgreement = true,
}: InvoiceFormProps) {
  const { errors } = useContext(InvoiceFormContext)
  const { t } = useTranslations('user')

  return (
    <View className={cn('w-full flex-1 gap-6', isPending && 'opacity-50', className)}>
      {errors.form && (
        <Text className="text-center text-xs text-destructive">{errors.form[0]}</Text>
      )}

      <View className="gap-4">
        <TextField
          label={t(($) => $.invoice.form.organisationName)}
          name="organisation_name"
          inputVariant={inputVariant}
        />
        <TextField
          label={t(($) => $.invoice.form.organisationIdentifier)}
          name="organisation_identifier"
          inputVariant={inputVariant}
        />
      </View>

      <View className="gap-4">
        <TextField
          label={t(($) => $.invoice.form.eInvoiceAddress)}
          name="e_invoice_address"
          inputVariant={inputVariant}
        />
        <TextField
          label={t(($) => $.invoice.form.eInvoiceOperator)}
          name="e_invoice_operator"
          inputVariant={inputVariant}
        />
      </View>

      <TextField
        label={t(($) => $.invoice.form.reference)}
        name="reference"
        description={t(($) => $.invoice.form.referenceDescription)}
        inputVariant={inputVariant}
      />

      {showAgreement && (
        <CheckboxField
          label={
            <Text className="text-sm">
              {t(($) => $.invoice.form.termsAgreement)}{' '}
              <Link href="/terms-of-service" className="underline" target="_blank">
                <Text className="text-accent underline hover:text-secondary">
                  {t(($) => $.invoice.form.termsLink)}
                </Text>
              </Link>
            </Text>
          }
          name="terms_agreement"
          inputVariant={inputVariant}
        />
      )}
    </View>
  )
}
